/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}

(function () {
  function wireWrapper(wrapper) {
    if (!wrapper || wrapper.__wired) return;
    wrapper.__wired = true;

    const btn = wrapper.querySelector('.js-mute-toggle');
    const video = wrapper.querySelector('video');

    if (!btn || !video) return;

    // Alku: autoplay vaatii muted
    video.muted = true;
    btn.setAttribute('aria-pressed', 'true'); // true = muted
    btn.setAttribute('aria-label', 'Unmute');

    // Kun video on valmiimpi, piilota kuvalayeri ja merkitse aktiiviseksi
    const hideImageLayer = () => {
      wrapper.classList.add('is-video-active');
      const mediaImageLayer = wrapper.parentElement?.querySelector('.media.media--has-overlay');
      if (mediaImageLayer) mediaImageLayer.classList.add('is-hidden-by-video');
    };
    video.addEventListener('loadeddata', hideImageLayer, { once: true });
    video.addEventListener('canplay', hideImageLayer, { once: true });

    // Yritä aloittaa toisto (hiljaisena) kun mahdollista
    const tryPlay = async () => { try { if (video.paused) await video.play(); } catch (e) {} };
    tryPlay();

    // Mute-toggle
    btn.addEventListener('click', async () => {
      const willUnmute = video.muted;
      try {
        if (willUnmute) {
          video.muted = false;
          video.volume = 1;
          await tryPlay(); // käyttäjäele -> sallittu
          document.querySelectorAll('.js-gallery-video').forEach(v => { if (v !== video) v.muted = true; });
          btn.setAttribute('aria-pressed', 'false');
          btn.setAttribute('aria-label', 'Mute');
        } else {
          video.muted = true;
          btn.setAttribute('aria-pressed', 'true');
          btn.setAttribute('aria-label', 'Unmute');
        }
      } catch (e) {
        console.warn('Unmute/play failed', e);
        video.muted = true;
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Unmute');
      }
    });
  }

  // 1) Alusta olemassa olevat wrapperit
  document.querySelectorAll('.video-audio-wrapper').forEach(wireWrapper);

  // 2) Reagoi kun <deferred-media> lisää <template>-sisällön
  const observer = new MutationObserver((muts) => {
    muts.forEach(m => {
      m.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        // Kun templatesta kopioitu video tulee sisään, wiretä wrapper
        if (node.matches('.video-audio-wrapper') || node.querySelector?.('.video-audio-wrapper')) {
          const w = node.matches('.video-audio-wrapper') ? node : node.querySelector('.video-audio-wrapper');
          wireWrapper(w);
        }
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // 3) Tuki teeman section-latauksille
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll?.('.video-audio-wrapper').forEach(wireWrapper);
  });
})();

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.js-mute-toggle').forEach(function (button) {
    button.addEventListener('click', function (event) {
      // Estetään linkin klikkaus
      event.preventDefault();
      event.stopPropagation();

      const blockId = button.dataset.blockId;
      const wrapper = document.querySelector(
        '.video-audio-wrapper[data-block-id="' + blockId + '"]'
      );

      if (!wrapper) return;

      const video = wrapper.querySelector('video, video-component video');
      if (!video) return;

      const willUnmute = video.muted; // jos nyt muted, klikillä UNMUTE
      video.muted = !video.muted;

      // Päivitä ARIA ja tilaluokat
      button.setAttribute('aria-pressed', willUnmute ? 'false' : 'true');
      button.setAttribute('aria-label', willUnmute ? 'Mute' : 'Unmute');

      // Vaihdetaan ikonit classin avulla
      button.classList.toggle('is-unmuted', willUnmute);
    });
  });
});

